/* eslint-disable react/prop-types */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import moment from 'moment';
import { Spinner, ErrorMessage } from '@gsa/afp-component-library';
import { useSetRecoilState } from 'recoil';
import {
  GET_SOLICITATION_BY_ID_WITH_CONTRACTS,
  GET_SIN_CLASSIFICATION,
} from '../../queries';
import {
  processOpenPeriod,
  processReopenPeriods,
} from '../../utils/open-reopen-period-util';
import { AUDIENCE_MAP } from '../../constants';
import {
  toStandardItems,
  toVehicleClassifications,
  toProgramDropdownOptions,
  withSavedPrograms,
} from './sin-classification-helpers';
import solicitationNotifications from '../../utils/solicitation-notification-helpers';

const EditSolicitationContext = createContext({});

// keeps a repeated empty response at one identity
const NO_ROWS = [];

const EditSolicitationProvider = ({ children, step }) => {
  const { id } = useParams();
  const [solicitation, setSolicitation] = useState(null);
  const [solicitationID, setSolicitationID] = useState(0);
  const [openPeriod, setOpenPeriod] = useState({});
  const [reopenPeriods, setReopenPeriods] = useState([]);
  const [currentPeriod, setCurrentPeriod] = useState({
    audience: AUDIENCE_MAP.Open,
  });
  const [contracts, setContracts] = useState([]);
  const [contractYear, setContractYear] = useState(new Date().getFullYear());
  const setSolicitationNotification = useSetRecoilState(
    solicitationNotifications,
  );

  // SIN selection state
  const [sinClassificationRows, setSinClassificationRows] = useState(NO_ROWS);
  const [modalMode, setModalMode] = useState(null);
  const [inactiveSIList, setInactiveSIList] = useState([]);

  useEffect(() => {
    // Clear all notifications when component mounts
    setSolicitationNotification((prev) =>
      prev.filter((notification) => notification.showOnNextPage),
    );
  }, []);

  const {
    loading: solicitationLoading,
    data: solicitationData,
    refetch: refetchSolicitation,
  } = useQuery(GET_SOLICITATION_BY_ID_WITH_CONTRACTS, {
    skip: !id,
    variables: { id: Number(id) },
    fetchPolicy: 'network-only',
  });

  // Process solicitation data whenever it changes (initial load or refetch)
  useEffect(() => {
    if (solicitationData?.getSolicitationById) {
      const current = solicitationData.getSolicitationById;
      setSolicitation(current);
      setSolicitationID(current.id);
      setContractYear(current.contractYear);

      const openPeriodTemp = processOpenPeriod([
        ...current.solicitationPeriods,
      ]);

      if (openPeriodTemp?.startDate) {
        setOpenPeriod(openPeriodTemp);
        setCurrentPeriod({
          startDate: moment(openPeriodTemp.startDate).format('YYYY-MM-DD'),
          endDate: moment(openPeriodTemp.endDate).format('YYYY-MM-DD'),
          startTime: moment(openPeriodTemp.startDate).format('HH:mm'),
          endTime: moment(openPeriodTemp.endDate).format('HH:mm'),
          audience: openPeriodTemp?.audience,
          vendors: openPeriodTemp?.solicitationPeriodVendors,
        });
      }

      const reopenPeriodsTemp = processReopenPeriods([
        ...current.solicitationPeriods,
      ]);
      setReopenPeriods(reopenPeriodsTemp);

      if (current.contracts && current.contracts.length > 0) {
        setContracts(current.contracts);
      }
    }
  }, [solicitationData]);

  // SIN classification query - only runs when step is 'sinSelect' and contractYear exists
  const { loading: sinClassificationLoading } = useQuery(
    GET_SIN_CLASSIFICATION,
    {
      skip: !contractYear || step !== 'sinSelect',
      variables: { year: [contractYear], isActive: [1] },
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      onCompleted: (data) => {
        setSinClassificationRows(data?.getSinClassification || NO_ROWS);
      },
    },
  );

  // Derived SIN data - memoized to prevent unnecessary recalculations
  const standardItems = useMemo(
    () => toStandardItems(sinClassificationRows),
    [sinClassificationRows],
  );

  const vehicleClassifications = useMemo(
    () => toVehicleClassifications(sinClassificationRows),
    [sinClassificationRows],
  );

  const programDropdownOptions = useMemo(
    () =>
      withSavedPrograms(
        toProgramDropdownOptions(sinClassificationRows),
        solicitation?.programs,
      ),
    [sinClassificationRows, solicitation?.programs],
  );

  const adjacentReopenPeriodStartDate = useMemo(
    () =>
      processReopenPeriods(solicitation?.solicitationPeriods || [])?.[0]
        ?.startDate,
    [solicitation?.solicitationPeriods],
  );

  const contextValue = useMemo(
    () => ({
      solicitation,
      solicitationID,
      openPeriod,
      reopenPeriods,
      currentPeriod,
      contractYear,
      contracts,
      refetchSolicitation,
      adjacentReopenPeriodStartDate,
      // SIN selection data
      sinClassificationLoading,
      standardItems,
      vehicleClassifications,
      programDropdownOptions,
      modalMode,
      setModalMode,
      inactiveSIList,
      setInactiveSIList,
    }),
    [
      solicitation,
      solicitationID,
      openPeriod,
      reopenPeriods,
      currentPeriod,
      contractYear,
      contracts,
      refetchSolicitation,
      adjacentReopenPeriodStartDate,
      sinClassificationLoading,
      standardItems,
      vehicleClassifications,
      programDropdownOptions,
      modalMode,
      inactiveSIList,
    ],
  );

  if (
    solicitationLoading ||
    (step === 'sinSelect' && sinClassificationLoading)
  ) {
    return (
      <div className="margin-top-8">
        <Spinner />
      </div>
    );
  }

  if (id && !solicitation) {
    return <ErrorMessage>Solicitation not found</ErrorMessage>;
  }

  return (
    <EditSolicitationContext.Provider value={contextValue}>
      {children}
    </EditSolicitationContext.Provider>
  );
};

export default EditSolicitationProvider;

export const useEditSolicitation = () => useContext(EditSolicitationContext);
